import * as BABYLON from "@babylonjs/core/Legacy/legacy";

import { config } from "./config.js";
import { PathMeshType } from "./path/common.js";
import {
	PathSegment,
	PathSegmentType,
	PathSegmentOptions,
} from "./path/segment.js";
import { MeshPool } from "./path/meshPool.js";
import { Player } from "./player.js";
import { GameScene } from "./scene.js";

const log = console.log.bind(console);

export class Path {
	private curSegment: PathSegment;
	private meshPool: MeshPool;
	private player: Player;

	gameScene: GameScene;
	bbScene: BABYLON.Scene;
	camera: BABYLON.Camera;
	private engine: BABYLON.Engine;

	constructor(
		scene: GameScene,
		engine: BABYLON.Engine,
		camera: BABYLON.Camera,
		player: Player,
		curSegment: PathSegment,
	) {
		this.engine = engine;
		this.curSegment = curSegment;
		this.meshPool = new MeshPool(scene);
		this.gameScene = scene;
		this.bbScene = scene.bbScene;
		this.camera = camera;
		this.player = player;

		this.loadAssets(engine);
	}

	getCurrentSegment(): PathSegment {
		return this.curSegment;
	}

	loadAssets(engine: BABYLON.Engine) {
		this.meshPool.loadAssets(engine);
		this.fillSegments();
	}

	getVisibleSegments(): PathSegment[] {
		let cur = this.curSegment;
		let res = [cur];
		let showNum = 8;
		for (let i = 0; i < showNum; i++) {
			if (cur === null) {
				break;
			}

			if (cur.pathMesh === null) {
				break;
			}

			res.push(cur);
			cur = cur.next;
		}

		return res;
	}

	fillSegments() {
		let cur = this.curSegment;
		let showNum = 15;
		for (let i = 0; i < showNum; i++) {
			if (cur === null) {
				break;
			}

			if (cur.pathMesh !== null) {
				cur = cur.next;
				continue;
			}

			cur.setup(this.player, this.meshPool, this.gameScene, this.camera);
			cur = cur.next;
		}
	}

	update() {
		// 根据位置显示新的路
		this.updateShowPath();

		// 每个已显示的路段做更新
		this.updateSegments();
	}

	updateShowPath() {
		let playerPos: BABYLON.Vector2 = this.player.pos2D();

		// 如果 curSegment 包含玩家位置
		if (this.curSegment.contain(playerPos)) {
			return;
		}

		// 1. 那么把 curSegment.prev 的 mesh 回收到 pool
		let i = 0;
		let segOld: PathSegment = this.curSegment.prev;
		while (i < 1) {
			if (segOld !== null) {
				segOld = segOld.prev;
			} else {
				break;
			}
			i += 1;
		}
		if (segOld !== null) {
			segOld.releaseMesh(this.meshPool);
		}

		// 2. 确定当前 curSegment
		let cur = this.curSegment;
		while (cur !== null) {
			if (cur.contain(playerPos)) {
				break;
			}
			cur = cur.next;
		}
		if (cur === null) {
			this.curSegment.speed.set(0, 0, 0);
			return;
		}

		this.curSegment = cur;

		// 3. 用新的 mesh 填充新的 pathSegment
		this.fillSegments();
	}

	updateSegments() {
		let segs = this.getVisibleSegments();
		for (let i = 0; i < segs.length; i++) {
			const seg = segs[i];
			// curSegment 金币碰撞 拐弯
			seg.update();
		}
	}

	checkCollision() {
		let segs = this.getVisibleSegments();
		for (let i = 0; i < segs.length; i++) {
			const seg = segs[i];
			// curSegment 金币碰撞 拐弯
			seg.checkCollision();
		}
	}

	turn() {
		// 从 当前路段curSeg 以及 next 找拐弯的路段
		// 如果拐弯路段是 turnRight, 那么在 路段标记右拐成功
		let cur = this.curSegment;
		if (cur.isTurn()) {
			cur.markTurnSuccess();
			return;
		}

		if (cur.next !== null) {
			if (cur.next.isTurn()) {
				cur.next.markTurnSuccess();
				return;
			}

			if (cur.next.next !== null) {
				if (cur.next.next.isTurn()) {
					cur.next.next.markTurnSuccess();
					return;
				}
			}
		}
	}
}

export function createPath(
	scene: GameScene,
	engine: BABYLON.Engine,
	camera: BABYLON.Camera,
	player: Player,
): Path {
	const confs = [];
	let n = config.roadLength;
	let options = new Map<string, boolean>();

	// 直线
	for (let i = 0; i < n; i++) {
		let options = new Map<string, boolean>();
		let meshType = PathMeshType.straight;
		// if (i % 10 >= 7) {
		//     options.set("fire", true)
		// }
		if (i > 0) {
			// options.set("fire", true)
			options.set("coin", true);
		}

		// if (i % 10 === 8) {
		//     meshType = PathMeshType.breakDown
		// } else if (i % 10 === 9) {
		//     meshType = PathMeshType.breakUp
		// }
		//          类型, 位置x, 位置z, 速度方向, mesh 类型, 道具
		confs.push([
			PathSegmentType.straight,
			0,
			i * 1,
			"+z",
			meshType,
			options,
		]);
	}

	// 拐右 (0, 2)
	confs.push([
		PathSegmentType.turnRight,
		0,
		n,
		"+z;+x",
		PathMeshType.turnRight,
		options,
	]);

	// 直线
	for (let i = 0; i < n; i++) {
		let options = new Map<string, boolean>();
		let meshType = PathMeshType.straight;
		if (i % 2 === 1) {
			options.set("fire", true);
		}
		// if (i % 10 === 8) {
		//     meshType = PathMeshType.breakDown
		// } else if (i % 10 === 9) {
		//     meshType = PathMeshType.breakUp
		// }
		//          类型, 位置x, 位置z, 速度方向
		confs.push([
			PathSegmentType.straight,
			i + 1,
			n,
			"+x",
			meshType,
			options,
		]);
	}

	// 拐左 (3, 2)
	confs.push([
		PathSegmentType.turnLeft,
		n + 1,
		n,
		"+x;+z",
		PathMeshType.turnLeft,
		options,
	]);

	// 直线
	for (let i = 0; i < n; i++) {
		let options = new Map<string, boolean>();
		if (i > 0) {
			// options.set("fire", true)
			options.set("coin", true);
		}
		//          类型, 位置x, 位置z, 速度方向
		confs.push([
			PathSegmentType.straight,
			n + 1,
			n + 1 + i,
			"+z",
			PathMeshType.straight,
			options,
		]);
	}

	// 拐左 (3, 5)
	confs.push([
		PathSegmentType.turnLeft,
		n + 1,
		2 * n + 1,
		"+z;-x",
		PathMeshType.turnLeft,
		options,
	]);

	// 直线
	for (let i = 0; i < n; i++) {
		if (i > 0) {
			// options.set("fire", true)
			options.set("coin", true);
		}
		//          类型, 位置x, 位置z, 速度方向
		confs.push([
			PathSegmentType.straight,
			n - i,
			2 * n + 1,
			"-x",
			PathMeshType.straight,
			options,
		]);
	}

	// 拐右 (0, 5)
	confs.push([
		PathSegmentType.turnRight,
		0,
		2 * n + 1,
		"-x;+z",
		PathMeshType.turnRight,
		options,
	]);

	// 直线
	for (let i = 0; i < n; i++) {
		if (i > 0) {
			// options.set("fire", true)
			options.set("coin", true);
		}
		//          类型, 位置x, 位置z, 速度方向
		confs.push([
			PathSegmentType.straight,
			0,
			2 * n + 2 + i,
			"+z",
			PathMeshType.straight,
			options,
		]);
	}

	let segs = [];
	for (let i = 0; i < confs.length; i++) {
		const conf = confs[i];
		let options = new PathSegmentOptions();
		let opt = conf[5] as Map<string, boolean>;
		if (opt.has("coin")) {
			options.coin = true;
		} else if (opt.has("fire")) {
			options.fire = true;
		}
		let seg = new PathSegment(
			conf[0],
			conf[1],
			conf[2],
			conf[3],
			conf[4],
			options,
		);
		segs.push(seg);

		// 串前面的
		if (i > 0) {
			seg.prev = segs[i - 1];
		}
	}

	// 串起来
	for (let i = 0; i < segs.length; i++) {
		let seg = segs[i];
		if (i < segs.length - 1) {
			seg.next = segs[i + 1];
		}
	}

	// log('segs', segs)

	let path = new Path(scene, engine, camera, player, segs[0]);
	return path;
}
